import { z } from "zod";

/**
 * Fields of the chart element (`type: "chart"`, docs/04-dokumentformat.md).
 * Kept apart from schema.ts so the element union stays small; schema.ts adds
 * the common element fields. No import from schema.ts (no import cycle).
 */

/** Chart kinds, in the order of the kind switch. */
export const CHART_KINDS = ["column", "bar", "line", "area", "pie", "donut", "progress"] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

export const CHART_KIND_LABELS: Record<ChartKind, string> = {
  column: "Säulen",
  bar: "Balken",
  line: "Linie",
  area: "Fläche",
  pie: "Kreis",
  donut: "Ring",
  progress: "Fortschritt",
};

export const CHART_LEGEND_POSITIONS = ["none", "top", "bottom", "right"] as const;
export type ChartLegendPosition = (typeof CHART_LEGEND_POSITIONS)[number];

/** Upper bounds: enough for any readable chart, small enough for the document size. */
export const CHART_MAX_LABELS = 50;
export const CHART_MAX_SERIES = 8;
export const CHART_LABEL_MAX_LENGTH = 80;

const hexSchema = z
  .string()
  .regex(
    /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    "Farbe als Hex-Wert, z. B. #1a2b3c",
  );

/** Finite numbers; `null` is a gap (no value for that label). */
const chartValueSchema = z
  .number()
  .refine((value) => Number.isFinite(value) && Math.abs(value) < 1e15, "Ungültiger Wert")
  .nullable();

export const chartSeriesSchema = z.object({
  name: z.string().max(CHART_LABEL_MAX_LENGTH).default(""),
  /** One value per label (missing values count as gaps). */
  values: z.array(chartValueSchema).max(CHART_MAX_LABELS),
  /** Own colour; otherwise the palette colour at the series' position. */
  color: hexSchema.optional(),
});

export const chartFields = {
  kind: z.enum(CHART_KINDS).default("column"),
  title: z.string().max(200).default(""),
  /** Categories (x axis; slices of pie and donut charts). */
  labels: z.array(z.string().max(CHART_LABEL_MAX_LENGTH)).max(CHART_MAX_LABELS),
  series: z.array(chartSeriesSchema).min(1).max(CHART_MAX_SERIES),
  /** Columns, bars and areas: series stacked on top of each other. */
  stacked: z.boolean().default(false),
  /**
   * Colours for series (slices of pie/donut). Resolved values; with
   * `brandPalette` they are refreshed from the brand kit (brand/apply.ts).
   */
  palette: z.array(hexSchema).min(1).max(12).optional(),
  brandPalette: z.boolean().default(false),
  legend: z.enum(CHART_LEGEND_POSITIONS).default("bottom"),
  showValues: z.boolean().default(false),
  /** Decimal places of value labels and the axis. */
  decimals: z.number().int().min(0).max(4).default(0),
  /** Unit after the numbers, e.g. "%" or "€" (German style: with a space). */
  unit: z.string().max(12).default(""),
  gridlines: z.boolean().default(true),
  axis: z.boolean().default(true),
  /** Upper end of the value axis (auto when missing); target of a progress ring. */
  max: z.number().positive().max(1e15).optional(),
  fontFamily: z.string().max(100).default("Inter"),
  /** Base text size in px (axis labels, legend); the title is larger. */
  fontSize: z.number().min(4).max(400).default(28),
  /** Text and axis colour (`brand.color`). */
  color: hexSchema.default("#3f3f46"),
  /** Corner radius of columns and bars in px. */
  barRadius: z.number().min(0).max(500).default(8),
  /** Donut and progress ring: hole as share of the radius. */
  hole: z.number().min(0.2).max(0.9).default(0.6),
  /** Lines: curved (monotone, never overshoots) instead of straight segments. */
  smooth: z.boolean().default(true),
  points: z.boolean().default(true),
  lineWidth: z.number().min(0.5).max(60).default(6),
};
